/**
 * UpcomingSessionsStrip — next curators going live.
 *
 * Redesigned (iter_338d) to mirror the "Listen to ONE VOICE RADIO" hero
 * aesthetic (see `pages/Home.js` radio section): centered pill → eyebrow
 * → massive 3-line title → gradient divider with subtitle.
 *
 * Reviewer profile-picture FIX: the backend returns a raw filename like
 * `"abc.png"`, which the browser was resolving against the current route
 * (`onevoicedistro.com/home/abc.png` → 404 → broken "?" placeholder). We
 * now route every picture through `getProfilePicUrl()` which builds a
 * stable `/api/profile/picture/{filename}` proxy URL.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Users, Zap, ArrowRight, Radio } from 'lucide-react';
import { getProfilePicUrl } from '../../utils/profilePic';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_CHIPS = {
  music: { color: '#00CCFF', label: 'Music' },
  beat: { color: '#ff69b4', label: 'Beat' },
  ai: { color: '#9b59b6', label: 'AI' },
  mentorship: { color: '#00ff88', label: 'Mentorship' },
  art: { color: '#FFDA33', label: 'Art' },
  video: { color: '#ff6b6b', label: 'Video' },
  comedy: { color: '#ff8c00', label: 'Comedy' },
};

const REVIEWER_VIGNETTE = 'linear-gradient(180deg,transparent 0%,rgba(0,0,0,0.8) 100%)';

const humanStart = (iso) => {
  if (!iso) return { label: 'Live now', live: true };
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return { label: 'Live now', live: true };
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  if (h >= 24) return { label: `in ${Math.floor(h / 24)}d`, live: false };
  if (h > 0) return { label: `in ${h}h${m ? ` ${m}m` : ''}`, live: false };
  return { label: `in ${m}m`, live: false };
};

/** Hero header — shared between populated and skeleton states. */
const UpcomingHero = () => (
  <div className="mb-8 text-center md:text-left" data-testid="upcoming-headline">
    <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#00CCFF]/40 bg-[#00CCFF]/10 mb-3">
      <Radio size={12} className="text-[#00CCFF] animate-pulse" />
      <span className="text-[#00CCFF] text-[10px] font-mono uppercase tracking-[0.3em]">Going Live Soon</span>
    </div>
    <h2 className="font-['Chakra_Petch'] uppercase leading-[0.95] tracking-tight">
      <span className="block text-xl md:text-2xl text-[#888] font-mono tracking-[0.5em]">Jump the queue</span>
      <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black mt-1 text-white">
        Upcoming <span className="text-[#00CCFF]">Sessions</span>
      </span>
    </h2>
    <div className="mt-4 flex items-center justify-center md:justify-start gap-3">
      <div className="h-px flex-1 max-w-[180px] bg-gradient-to-r from-transparent via-[#00CCFF]/60 to-transparent" />
      <p className="text-[#888] font-mono text-[11px] uppercase tracking-widest">Next curators going live</p>
      <div className="h-px flex-1 max-w-[180px] bg-gradient-to-r from-transparent via-[#00ff88]/60 to-transparent" />
    </div>
  </div>
);

const SESSION_SKELETON_GRADIENTS = [
  'from-[#002a3a] via-[#001c24] to-[#0a0a0a]',
  'from-[#001c24] via-[#002a3a] to-[#0a0a0a]',
  'from-[#003a4a] via-[#001c24] to-[#0a0a0a]',
  'from-[#0a0a0a] via-[#002a3a] to-[#003a4a]',
];

export const UpcomingSessionsStrip = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brokenIds, setBrokenIds] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/music-review/active-sessions`, {
          params: { upcoming: true, limit: 12 },
        });
        if (!cancelled) setSessions(res.data?.sessions || []);
      } catch {
        /* silent */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const visible = sessions
    .map((s) => ({
      ...s,
      _pic: getProfilePicUrl(s.reviewer_profile_picture || s.reviewer_avatar || null, s.reviewer_id),
    }))
    .filter((s) => s._pic && !brokenIds.has(s.id));

  // Hide the entire section when there's nothing to show.
  // The home page used to render a "Curators warming up…" skeleton even
  // when zero reviewers were live or scheduled — that visually implied
  // sessions were imminent when in reality the queue was empty. User
  // request: only render this section when at least one reviewer is
  // live OR has a session scheduled. A still-loading state ALSO renders
  // null so the home page never shows a misleading skeleton.
  if (loading || visible.length === 0) {
    return null;
  }

  return (
    <section
      className="preserve-dark relative mb-12 py-10 border border-[#1a1a1a] rounded-lg overflow-hidden bg-[#050505] w-full"
      data-testid="upcoming-sessions-strip"
    >
      {/* Ambient halo backdrop — matches OneVoice Radio + Radar */}
      <div aria-hidden className="absolute inset-0 pointer-events-none opacity-70">
        <div className="absolute -top-24 left-1/4 w-[520px] h-[520px] rounded-full bg-[#00CCFF]/10 blur-3xl" />
        <div className="absolute -bottom-32 right-1/4 w-[520px] h-[520px] rounded-full bg-[#00ff88]/10 blur-3xl" />
      </div>

      <div className="w-full px-4 sm:px-6 md:px-8 relative">
        <UpcomingHero />

        {/* ═══ Horizontal snap scroller ═══ */}
        <div className="flex items-center justify-between mb-4">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#555]">
            {visible.length} curator{visible.length === 1 ? '' : 's'} queued
          </span>
          <Link
            to="/music-review"
            className="hidden sm:inline-flex text-[#00CCFF] font-mono text-xs uppercase tracking-wider hover:text-white transition-colors items-center gap-1"
            data-testid="upcoming-see-all"
          >
            See all <ArrowRight size={14} />
          </Link>
        </div>

        <div
          className="relative flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory"
          style={{ scrollbarWidth: 'thin' }}
        >
          {visible.map((s) => {
            const queueDepth = s.queue_depth ?? s.submission_count ?? 0;
            const tiers = (s.priority_tiers || []).filter((t) => t.enabled);
            const cheapest = tiers.length
              ? Math.min(...tiers.map((t) => Number(t.price) || 0))
              : Number(s.priority_price) || 5;
            const cats = Array.isArray(s.accepted_categories) && s.accepted_categories.length
              ? s.accepted_categories
              : ['music'];
            const time = humanStart(s.starts_at || s.created_at);

            return (
              <Link
                key={s.id}
                to={`/review/${s.code}?tab=submit`}
                className="snap-start flex-shrink-0 w-[260px] sm:w-[280px] h-[360px] relative rounded-md overflow-hidden bg-[#0a0a0a] border border-[#1a1a1a] hover:border-[#00CCFF]/70 hover:shadow-[0_0_32px_rgba(0,204,255,0.25)] transition-all duration-300 group"
                data-testid={`upcoming-card-${s.code}`}
              >
                {/* Hero reviewer picture — ~70% of the card */}
                <div className="absolute inset-x-0 top-0 h-[70%] overflow-hidden bg-black">
                  <img
                    src={s._pic}
                    alt={s.reviewer_username}
                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                    loading="lazy"
                    onError={() => setBrokenIds((prev) => {
                      if (prev.has(s.id)) return prev;
                      const next = new Set(prev);
                      next.add(s.id);
                      return next;
                    })}
                  />
                  <div className="absolute inset-x-0 bottom-0 h-1/2" style={{ background: REVIEWER_VIGNETTE }} />

                  <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-[3px] bg-black/80 backdrop-blur-sm border border-[#00ff88]/40 rounded-sm">
                    <span className={`w-1.5 h-1.5 rounded-full ${time.live ? 'bg-[#00ff88] animate-pulse' : 'bg-[#00CCFF]'}`} />
                    <span className={`font-mono text-[9px] uppercase tracking-wider ${time.live ? 'text-[#00ff88]' : 'text-[#00CCFF]'}`}>
                      {time.label}
                    </span>
                  </div>

                  <div className="absolute bottom-3 left-3 right-3">
                    <p className="text-white font-['Chakra_Petch'] text-base font-bold leading-tight truncate group-hover:text-[#00CCFF] transition-colors drop-shadow-[0_1px_4px_rgba(0,0,0,0.8)]">
                      @{s.reviewer_username}
                    </p>
                  </div>
                </div>

                {/* Metadata tray — bottom 30% */}
                <div className="absolute inset-x-0 bottom-0 h-[30%] p-3 flex flex-col justify-between bg-[#0a0a0a]">
                  <p className="text-white font-['Chakra_Petch'] text-sm leading-tight line-clamp-2">
                    {s.title || 'Review Session'}
                  </p>

                  <div className="flex flex-wrap gap-1">
                    {cats.slice(0, 3).map((c) => {
                      const chip = CATEGORY_CHIPS[c] || { color: '#888', label: c };
                      return (
                        <span
                          key={c}
                          className="px-1.5 py-[2px] text-[9px] font-mono uppercase tracking-wider rounded"
                          style={{ color: chip.color, border: `1px solid ${chip.color}66`, background: `${chip.color}15` }}
                        >
                          {chip.label}
                        </span>
                      );
                    })}
                  </div>

                  <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider pt-1 border-t border-[#1a1a1a]">
                    <span className="text-[#888] flex items-center gap-1">
                      <Users size={11} /> {queueDepth} in line
                    </span>
                    <span className="text-[#FFDA33] flex items-center gap-1 font-bold">
                      <Zap size={11} /> Skip ${cheapest.toFixed(0)}+
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default UpcomingSessionsStrip;
